import { useEffect } from 'react'

import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'

import { apiGetAllTokens } from '@src/api/machprotocol.api'
import {
    CHAIN_IDS,
    ChainId,
    ChainName,
    NATIVE_TOKEN_ADDRESS,
    WRAPPED_GAS_TOKENS,
    getChainId,
    getGasTokenSymbol,
} from '@src/configs/chainInfo'
import { HARDCODED_TESTNET_TOKEN_DATA, PUBLIC_STATIC_LOGOS } from '@src/configs/tokens'
import { TOKENS_REFRESH_INTERVAL } from '@src/constants/application'
import { Address, IToken } from '@src/types/token'
import { bigintPow, createContextLogger } from '@src/utils'
import { isFeatureEnabled, isV2SupportedChain } from '@src/utils/environment'
import { useTokensStore } from '@src/zustand_stores'

const logger = createContextLogger('useFetchAllTokens')

const loadAllTokens = async (): Promise<{ tokens: IToken[]; nativeTokenPricesInUSD: Record<number, number> }> => {
    logger.info('🔄 Starting token loading process...')

    const tokens: IToken[] = []
    const nativeTokenPricesInUSD: Record<number, number> = {}

    try {
        logger.info('📡 Fetching tokens from API...')
        const loadedTokens = await apiGetAllTokens()
        logger.info('📦 Received tokens data:', loadedTokens)
        // logger.debug('loadedBalances', loadedTokens)

        for (const _networkName of Object.keys(loadedTokens)) {
            const networkName = _networkName as ChainName
            const chainId = CHAIN_IDS[networkName]
            nativeTokenPricesInUSD[chainId] = loadedTokens[networkName].native.current_price // Add native price to the list even if chain is feature gated.

            if (!isV2SupportedChain(chainId)) {
                continue
            }

            if (!isFeatureEnabled('SHOW_TRON_CHAIN') && chainId === ChainId.Tron) {
                continue
            }

            // Add native gas & wrapped native gas tokens
            if (loadedTokens[networkName].native) {
                // FE-546
                // NOTE: we DO NOT want to support: wrapping/unwrapping on CELO
                // CELO is a modified EVM chain wherein the celo erc20 token is 1:1 with
                // the native gas asset without wrapping/unwrapping
                // therefore we add the erc20 token as the native asset and we do not add any WCELO token.
                if (networkName == 'celo') {
                    tokens.push({
                        symbol: `${getGasTokenSymbol(networkName)}`,
                        logoURI: `/assets/token/${getGasTokenSymbol(networkName).toLowerCase()}.png`,
                        name: `${getGasTokenSymbol(networkName)}`,
                        chainId: chainId,
                        address: NATIVE_TOKEN_ADDRESS[CHAIN_IDS[networkName as ChainName]],
                        decimals: loadedTokens[networkName].native.decimals,
                        native: true,
                        wrapped: false,
                        isStableCoin: false,
                        price_change_24h: loadedTokens[networkName].native.price_change_24h,
                        price_change_percentage_24h: loadedTokens[networkName].native.price_change_percentage_24h,
                        price_current: loadedTokens[networkName].native.current_price,
                        market_cap: loadedTokens[networkName].native.market_cap,
                    })
                } else {
                    tokens.push({
                        symbol: `${getGasTokenSymbol(networkName)}`,
                        logoURI: `/assets/token/${getGasTokenSymbol(networkName).toLowerCase()}.png`,
                        name: `${getGasTokenSymbol(networkName)}`,
                        chainId: chainId,
                        address: NATIVE_TOKEN_ADDRESS[CHAIN_IDS[networkName as ChainName]],
                        decimals: loadedTokens[networkName].native.decimals,
                        native: true,
                        wrapped: false,
                        isStableCoin: false,
                        price_change_24h: loadedTokens[networkName].native.price_change_24h,
                        price_change_percentage_24h: loadedTokens[networkName].native.price_change_percentage_24h,
                        price_current: loadedTokens[networkName].native.current_price,
                        market_cap: loadedTokens[networkName].native.market_cap,
                    })

                    // Add wrapped gas token if available
                    if (
                        WRAPPED_GAS_TOKENS[getChainId(networkName)] &&
                        WRAPPED_GAS_TOKENS[getChainId(networkName)].address
                    ) {
                        tokens.push({
                            symbol: `W${getGasTokenSymbol(networkName)}`,
                            logoURI: `/assets/token/w${getGasTokenSymbol(networkName).toLowerCase()}.png`,
                            name: `W${getGasTokenSymbol(networkName)}`,
                            chainId: chainId,
                            address: WRAPPED_GAS_TOKENS[getChainId(networkName)].address,
                            decimals: loadedTokens[networkName].native.decimals,
                            native: false,
                            wrapped: true,
                            isStableCoin: false,
                            price_change_24h: loadedTokens[networkName].native.price_change_24h,
                            price_change_percentage_24h: loadedTokens[networkName].native.price_change_percentage_24h,
                            price_current: loadedTokens[networkName].native.current_price,
                            market_cap: loadedTokens[networkName].native.market_cap,
                        })
                    }
                }
            }

            for (const tokenAddress of Object.keys(loadedTokens[networkName])) {
                try {
                    if (
                        tokenAddress !== 'native' &&
                        WRAPPED_GAS_TOKENS[getChainId(networkName)] &&
                        WRAPPED_GAS_TOKENS[getChainId(networkName)].address.toLowerCase() !== tokenAddress.toLowerCase()
                    ) {
                        let logo: string = ''

                        const publicLogoKey = PUBLIC_STATIC_LOGOS[networkName]
                            ? Object.keys(PUBLIC_STATIC_LOGOS[networkName]).find(o => {
                                  o.toLowerCase() == tokenAddress.toLowerCase()
                              })
                            : null

                        if (publicLogoKey) {
                            logo = PUBLIC_STATIC_LOGOS[networkName][publicLogoKey]
                        } else {
                            logo = loadedTokens[networkName][tokenAddress].image ?? ''
                        }

                        // Debug log for Solana chain tokens
                        if (chainId === ChainId.Solana) {
                            // logger.debug('Processing Solana token', {
                            //     networkName,
                            //     tokenAddress,
                            //     tokenData: loadedTokens[networkName][tokenAddress],
                            //     logo,
                            // })
                        }

                        // Do not add tokens with missing decimals.
                        if (
                            typeof loadedTokens[networkName][tokenAddress].decimals != 'number' ||
                            loadedTokens[networkName][tokenAddress].decimals < 0
                        ) {
                            logger.warn('Bad token data from server: token has no decimals or decimals < 0', {
                                [networkName]: {
                                    [tokenAddress]: loadedTokens[networkName][tokenAddress],
                                },
                            })
                        } else {
                            tokens.push({
                                symbol: loadedTokens[networkName][tokenAddress].symbol.toUpperCase(),
                                logoURI: logo,
                                name: loadedTokens[networkName][tokenAddress].name,
                                chainId: chainId,
                                address: tokenAddress as Address,
                                decimals: loadedTokens[networkName][tokenAddress].decimals,
                                native: false,
                                wrapped: false,
                                isStableCoin: false,
                                price_change_24h: loadedTokens[networkName][tokenAddress].price_change_24h,
                                price_change_percentage_24h:
                                    loadedTokens[networkName][tokenAddress].price_change_percentage_24h,
                                price_current: loadedTokens[networkName][tokenAddress].current_price,
                                market_cap: loadedTokens[networkName][tokenAddress].market_cap,
                            })
                        }
                    }
                } catch (e) {
                    logger.error('Error processing token', { error: e })
                }
            }
        }
        return { tokens: tokens, nativeTokenPricesInUSD: nativeTokenPricesInUSD }
    } catch (error) {
        const errorDetails = {
            message: (error as Error).message,
            stack: (error as Error).stack,
            name: (error as Error).name,
            response: (error as any).response?.data,
            status: (error as any).response?.status,
            headers: (error as any).response?.headers,
        }

        // Send to Sentry with additional context
        Sentry.withScope(scope => {
            scope.setTag('feature', 'token_loading')
            scope.setLevel('error')
            scope.setContext('api_response', {
                response: errorDetails.response,
                status: errorDetails.status,
                headers: errorDetails.headers,
            })
            scope.setContext('state', {
                currentTokens: tokens.length,
            })
            Sentry.captureException(error)
        })

        logger.error('Failed to load tokens', { error: errorDetails })
        logger.error('🔍 Full error object:', error)

        // If API fails, fall back to hardcoded data for testnets
        if (isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY')) {
            logger.info('🧪 API failed, falling back to hardcoded testnet data')
            return loadHardcodedTestnetTokens()
        }

        throw error
    }
}

/**
 * Helper function to load tokens from static data for testnets
 */
const loadHardcodedTestnetTokens = (): { tokens: IToken[]; nativeTokenPricesInUSD: Record<number, number> } => {
    logger.info('Loading static testnet token data')

    const tokens: IToken[] = []
    const nativeTokenPricesInUSD: Record<number, number> = {}

    // Process each testnet in HARDCODED_TESTNET_TOKEN_DATA
    for (const _networkName of Object.keys(HARDCODED_TESTNET_TOKEN_DATA)) {
        const networkName = _networkName as ChainName
        const chainId = CHAIN_IDS[networkName]
        const staticData = HARDCODED_TESTNET_TOKEN_DATA[networkName]

        // Add native token price
        if (staticData.native) {
            nativeTokenPricesInUSD[chainId] = staticData.native.current_price
        }

        if (!isV2SupportedChain(chainId)) {
            continue
        }

        // Add native gas token
        if (staticData.native) {
            tokens.push({
                symbol: `${getGasTokenSymbol(networkName)}`,
                logoURI: `/assets/token/${getGasTokenSymbol(networkName).toLowerCase()}.png`,
                name: `${getGasTokenSymbol(networkName)}`,
                chainId: chainId,
                address: NATIVE_TOKEN_ADDRESS[CHAIN_IDS[networkName as ChainName]],
                decimals: staticData.native.decimals,
                native: true,
                wrapped: false,
                isStableCoin: false,
                price_change_24h: staticData.native.price_change_24h,
                price_change_percentage_24h: staticData.native.price_change_percentage_24h,
                price_current: staticData.native.current_price,
                market_cap: staticData.native.market_cap,

                // Dummy balance
                userBalance: BigInt(100) * bigintPow(BigInt(10), BigInt(6)),
                userBalanceInUSD: 1000,
            })

            // Add wrapped gas token if available
            if (WRAPPED_GAS_TOKENS[getChainId(networkName)] && WRAPPED_GAS_TOKENS[getChainId(networkName)].address) {
                tokens.push({
                    symbol: `W${getGasTokenSymbol(networkName)}`,
                    logoURI: `/assets/token/w${getGasTokenSymbol(networkName).toLowerCase()}.png`,
                    name: `W${getGasTokenSymbol(networkName)}`,
                    chainId: chainId,
                    address: WRAPPED_GAS_TOKENS[getChainId(networkName)].address,
                    decimals: staticData.native.decimals,
                    native: false,
                    wrapped: true,
                    isStableCoin: false,
                    price_change_24h: staticData.native.price_change_24h,
                    price_change_percentage_24h: staticData.native.price_change_percentage_24h,
                    price_current: staticData.native.current_price,
                    market_cap: staticData.native.market_cap,

                    // Dummy balance
                    userBalance: BigInt(100) * bigintPow(BigInt(10), BigInt(6)),
                    userBalanceInUSD: 1000,
                })
            }
        }

        // Add other tokens from static data
        if (staticData.assets) {
            for (const tokenAddress of Object.keys(staticData.assets)) {
                const tokenData = staticData.assets[tokenAddress]

                // Skip if this is the wrapped token we already added
                if (
                    WRAPPED_GAS_TOKENS[getChainId(networkName)] &&
                    WRAPPED_GAS_TOKENS[getChainId(networkName)].address.toLowerCase() === tokenAddress.toLowerCase()
                ) {
                    continue
                }

                tokens.push({
                    symbol: tokenData.symbol.toUpperCase(),
                    logoURI: tokenData.image || '',
                    name: tokenData.name,
                    chainId: chainId,
                    address: tokenAddress as Address,
                    decimals: tokenData.decimals,
                    native: false,
                    wrapped: false,
                    isStableCoin: ['USDC', 'USDT', 'DAI', 'BUSD'].includes(tokenData.symbol.toUpperCase()),
                    price_change_24h: tokenData.price_change_24h,
                    price_change_percentage_24h: tokenData.price_change_percentage_24h,
                    price_current: tokenData.current_price,
                    market_cap: tokenData.market_cap,

                    // Dummy balance
                    userBalance: BigInt(100) * bigintPow(BigInt(10), BigInt(6)),
                    userBalanceInUSD: 1000,
                })
            }
        }
    }

    logger.info(`Loaded ${tokens.length} static testnet tokens`)
    return { tokens, nativeTokenPricesInUSD }
}

/**
 * Tanstack query for fetching tokens
 */
function useAllTokensQuery() {
    return useQuery({
        queryKey: ['allTokens'],
        // Function to fetch data.
        // loadAllTokens resolves in an object of type { tokens: IToken[], nativeTokenPricesInUSD: Record<number, number> }
        queryFn: loadAllTokens,
        staleTime: TOKENS_REFRESH_INTERVAL,
        refetchInterval: TOKENS_REFRESH_INTERVAL,
        refetchOnWindowFocus: false,
    })
}

export function useFetchAllTokens() {
    const { data, isLoading, error } = useAllTokensQuery()

    const existingTokens = useTokensStore(state => state.tokens)

    const setTokens = useTokensStore(state => state.setTokens)
    const setNativeTokenPricesInUSD = useTokensStore(state => state.setNativeTokenPricesInUSD)
    const setStoreLoading = useTokensStore(state => state.setIsLoading)
    const setStoreError = useTokensStore(state => state.setError)

    useEffect(() => {
        // Sync loading & error states
        setStoreLoading(isLoading)
        setStoreError(error ? (error as Error).message : null)

        if (data) {
            // Merge existing tokens with new tokens, preserving user balances
            const mergedTokens = data.tokens.map(newToken => {
                const existing = existingTokens.find(
                    // Sometimes I got undefined has no function `toLowerCase`
                    t =>
                        t.address &&
                        newToken.address &&
                        t.address.toLowerCase() === newToken.address.toLowerCase() &&
                        t.chainId === newToken.chainId
                )

                return {
                    ...newToken,
                    // Preserve existing userBalance & userBalanceInUSD if available
                    userBalance: existing?.userBalance,
                    userBalanceInUSD: existing?.userBalanceInUSD,
                }
            })

            setTokens(mergedTokens)
            setNativeTokenPricesInUSD(data.nativeTokenPricesInUSD)
        }
    }, [data, isLoading, error, setTokens, setNativeTokenPricesInUSD, setStoreLoading, setStoreError])

    /**
     * @note We do not return `data` here; data is consumed via the Zustand store.
     */
    return { isLoading, error }
}
